import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  CircularProgress,
} from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";

interface Props {
  open: boolean;
  poNumber: string;
  onClose: () => void;
  onConfirm: (confirmPoNumber: string) => Promise<void>;
}

const DeletePurchaseOrderModal: React.FC<Props> = ({
  open,
  poNumber,
  onClose,
  onConfirm,
}) => {
  const [confirmPoNumber, setConfirmPoNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    if (!loading) {
      setConfirmPoNumber("");
      setError("");
      onClose();
    }
  };

  const handleConfirm = async () => {
    setError("");

    if (!confirmPoNumber.trim()) {
      setError("Please enter the PO number to confirm");
      return;
    }

    try {
      setLoading(true);
      await onConfirm(confirmPoNumber);
      handleClose();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          "Failed to delete purchase order. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const isValid = confirmPoNumber === poNumber;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color='error' />
          Delete Purchase Order
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity='error' sx={{ mb: 2 }}>
          <Typography variant='body2' gutterBottom>
            <strong>Warning:</strong> This action cannot be undone!
          </Typography>
          <Typography variant='body2'>This will permanently delete:</Typography>
          <ul style={{ marginTop: 8, marginBottom: 0 }}>
            <li>Purchase order #{poNumber}</li>
            <li>All line items associated with this PO</li>
          </ul>
        </Alert>

        <Typography variant='body2' gutterBottom sx={{ mb: 2 }}>
          To confirm deletion, please type the PO number{" "}
          <strong>{poNumber}</strong> below:
        </Typography>

        <TextField
          fullWidth
          label='PO Number'
          value={confirmPoNumber}
          onChange={(e) => setConfirmPoNumber(e.target.value)}
          disabled={loading}
          autoFocus
          error={!!error}
          helperText={error}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color='error'
          variant='contained'
          disabled={!isValid || loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? "Deleting..." : "Delete Purchase Order"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletePurchaseOrderModal;
